import { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import type {
  ReportCitationNode,
  ReportCitations,
} from '../types/report.types';
import {
  BODY_FG,
  CARD_BORDER,
  DONUT_TRACK_BG,
  FAINT_FG,
  FONT,
  HEADING_FG,
  INNER_RULE,
  MUTED_FG,
  NEGATIVE_BG,
  NEGATIVE_FG,
  POSITIVE_BG,
  POSITIVE_FG,
  SURFACE,
  WARN_BG,
  WARN_FG,
  WHITE,
  WORK_POST_BG,
  WORK_POST_FG,
  WORK_SERVICE_BG,
  WORK_SERVICE_FG,
} from '../reportTokens';

/**
 * The Citation Health section's full design — design lines 326-389. Renders
 * only what `CitationsSection` (ReportSections.tsx) shows once `citations`
 * is already known to be `enabled`; that wrapper still owns the "citation
 * management is not part of this plan" empty state, so nothing here needs to
 * re-check `enabled`.
 *
 * Every sub-block below degrades on its own missing field rather than the
 * whole section vanishing: a fresh subscription with no score yet still
 * shows the stat blocks and (once directories exist) the network, just
 * without a donut or a band pill to draw from nothing.
 */

/** design line 323's fixed "Foundation" tag — a tier label, not data the
 * backend sends (there is no such field on `ReportCitations`), so it is
 * fixed copy like `WORK_WHY` in ReportSections.tsx. */
export function FoundationEyebrow() {
  return (
    <Box
      component='span'
      sx={{
        fontSize: FONT.micro,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: WORK_POST_FG,
        bgcolor: WORK_POST_BG,
        px: 1.25,
        py: 0.5,
        borderRadius: '9999px',
      }}
    >
      Foundation
    </Box>
  );
}

/** The band pill's colour, keyed off the backend's own word rather than a
 * score threshold re-derived here — `citationScoreLabel` in
 * hostedReportSnapshot.js already owns those thresholds, and re-deriving
 * them here risks drifting from the sentence the pill sits next to. */
function bandTone(label?: string): { fg: string; bg: string } {
  if (label === 'Healthy') return { fg: POSITIVE_FG, bg: POSITIVE_BG };
  if (label === 'Improving') return { fg: WARN_FG, bg: WARN_BG };
  if (label === 'Needs work') return { fg: NEGATIVE_FG, bg: NEGATIVE_BG };
  return { fg: MUTED_FG, bg: SURFACE };
}

/** The score numeral is the one piece of type on this whole page bigger than
 * the shared `FONT` scale reaches (44px in the design) — a local constant
 * rather than a new shared token, since nothing else on the page needs it. */
const SCORE_FONT_SIZE = '2.75rem';
const DONUT_RADIUS = 52;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** The score ring — an SVG arc whose `stroke-dasharray` is cut to the score's
 * share of the circle, rotated -90deg so the arc starts at 12 o'clock like
 * the design's. Only rendered by `ScorePanel` when `score` is not null. */
function ScoreDonut({ score, tone }: { score: number; tone: string }) {
  // Scores are already 0-100 server-side; clamped here too so a future bad
  // value draws a bounded arc instead of overshooting the circle.
  const filled = Math.max(0, Math.min(100, score));
  const dash = (filled / 100) * DONUT_CIRCUMFERENCE;
  return (
    <Box
      sx={{ position: 'relative', width: 160, height: 160, flex: '0 0 auto' }}
    >
      <Box
        component='svg'
        viewBox='0 0 120 120'
        sx={{ width: 160, height: 160, transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={60}
          cy={60}
          r={DONUT_RADIUS}
          fill='none'
          stroke={DONUT_TRACK_BG}
          strokeWidth={15}
        />
        <circle
          cx={60}
          cy={60}
          r={DONUT_RADIUS}
          fill='none'
          stroke={tone}
          strokeWidth={15}
          strokeLinecap='round'
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE}`}
        />
      </Box>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          sx={{
            fontSize: SCORE_FONT_SIZE,
            fontWeight: 800,
            letterSpacing: '-0.03em',
            color: tone,
            lineHeight: 1,
          }}
        >
          {Math.round(filled)}
        </Typography>
        <Typography
          sx={{
            fontSize: FONT.micro,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: FAINT_FG,
            mt: 0.5,
          }}
        >
          out of 100
        </Typography>
      </Box>
    </Box>
  );
}

/** The donut plus the band pill, "Citation Health Score" heading and body —
 * design lines 330-346. `score === null` (no score yet, or a v2 snapshot
 * that predates one) drops only the donut; the heading and whatever body
 * copy exists still render, so the panel never collapses to nothing. */
function ScorePanel({ citations }: { citations: ReportCitations }) {
  const tone = bandTone(citations.scoreLabel);
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: INNER_RULE,
        borderRadius: '14px',
        p: { xs: 2.25, sm: 3 },
        display: 'flex',
        alignItems: 'center',
        gap: 3.5,
        flexWrap: 'wrap',
        minWidth: 0,
      }}
    >
      {citations.score !== null && (
        <ScoreDonut score={citations.score} tone={tone.fg} />
      )}
      <Box sx={{ minWidth: 220, flex: 1 }}>
        {citations.scoreLabel && (
          <Box
            component='span'
            sx={{
              display: 'inline-block',
              fontSize: FONT.fine,
              fontWeight: 700,
              color: tone.fg,
              bgcolor: tone.bg,
              px: 1.75,
              py: 0.75,
              borderRadius: '9999px',
              mb: 1.5,
            }}
          >
            {citations.scoreLabel}
          </Box>
        )}
        <Typography
          sx={{
            fontSize: FONT.cardTitle,
            fontWeight: 700,
            letterSpacing: '-0.015em',
            mb: 1,
          }}
        >
          Citation Health Score
        </Typography>
        <Typography
          sx={{ fontSize: FONT.body, color: MUTED_FG, lineHeight: 1.65 }}
        >
          {citations.scoreBody ||
            "Score not available yet. Check back once this week's directory scan finishes."}
        </Typography>
      </Box>
    </Box>
  );
}

/** "Accurate & live" and "In progress" — design lines 348-361. Values come
 * straight off `live`/`inProgress`, not the design mockup's own
 * `total - live` arithmetic, since the backend already counts "in progress"
 * listings directly rather than treating every non-live listing as pending. */
function StatBlocks({ citations }: { citations: ReportCitations }) {
  const inProgress = citations.inProgress;
  const stats: Array<{
    label: string;
    value: number;
    note: string;
    tone: string;
    spinner: boolean;
  }> = [
    {
      label: 'Accurate & live',
      value: citations.live,
      note: 'NAP matches everywhere',
      tone: POSITIVE_FG,
      spinner: false,
    },
    {
      label: 'In progress',
      value: inProgress,
      note: 'Submitted, awaiting confirmation',
      tone: inProgress > 0 ? WORK_SERVICE_FG : FAINT_FG,
      spinner: inProgress > 0,
    },
  ];

  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 0 }}
    >
      {stats.map(stat => (
        <Box
          key={stat.label}
          sx={{
            border: '1px solid',
            borderColor: INNER_RULE,
            borderLeft: `4px solid ${stat.tone}`,
            borderRadius: '12px',
            px: 2.25,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.75,
            flex: 1,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: FONT.figure,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.1,
                color: stat.tone,
              }}
            >
              {stat.value}
            </Typography>
            <Typography sx={{ fontSize: FONT.body, fontWeight: 700, mt: 0.5 }}>
              {stat.label}
            </Typography>
            <Typography sx={{ fontSize: FONT.fine, color: MUTED_FG, mt: 0.25 }}>
              {stat.note}
            </Typography>
          </Box>
          {/* Only "In progress" ever spins — nothing is pending on "Accurate
              & live" for a spinner to describe. */}
          {stat.spinner && (
            <CircularProgress
              size={22}
              thickness={5}
              sx={{ color: WORK_SERVICE_FG }}
            />
          )}
        </Box>
      ))}
    </Box>
  );
}

const CITATION_STATE_LABEL: Record<string, string> = {
  live: 'Live',
  in_progress: 'In progress',
  needs_attention: 'Needs attention',
};

/** Same three real states `services/CitationSyncService.js` writes, plus a
 * neutral fallback for `listing.state || 'unknown'` (hostedReportSnapshot.js)
 * — never thrown on, never guessed into one of the three real bands. */
function citationStateTone(state: string): { fg: string; bg: string } {
  if (state === 'live') return { fg: POSITIVE_FG, bg: POSITIVE_BG };
  if (state === 'in_progress')
    return { fg: WORK_SERVICE_FG, bg: WORK_SERVICE_BG };
  if (state === 'needs_attention') return { fg: WARN_FG, bg: WARN_BG };
  return { fg: MUTED_FG, bg: SURFACE };
}

/**
 * The directory's logo, or its initials when there is no logo or it fails to
 * load. The badge stays the same size either way, so a broken image never
 * shifts the row.
 *
 * For Google, Apple, Bing, Yelp and Foursquare the stored logo is a path into
 * the main app's assets (`KNOWN_DIRECTORY_LOGO_URLS`, CitationSyncService.js),
 * not a URL. This app serves copies at the same paths from
 * `public/assets/images/citation-logos/`, so those paths resolve here too,
 * on an agency's own domain included.
 */
function DirectoryMark({ node }: { node: ReportCitationNode }) {
  const [failed, setFailed] = useState(false);
  const showLogo = Boolean(node.logoUrl) && !failed;
  return (
    <Box
      aria-hidden
      sx={{
        width: 34,
        height: 34,
        flex: '0 0 auto',
        borderRadius: '8px',
        bgcolor: WHITE,
        border: '1px solid',
        borderColor: CARD_BORDER,
        color: BODY_FG,
        fontSize: FONT.fine,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        p: showLogo ? '4px' : 0,
      }}
    >
      {showLogo ? (
        <Box
          component='img'
          src={node.logoUrl as string}
          alt={node.name}
          loading='lazy'
          onError={() => setFailed(true)}
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      ) : (
        node.initials
      )}
    </Box>
  );
}

/**
 * The order the network is drawn in: live listings first, then the work
 * still under way. A client opening the report sees what is already working
 * before what is not, and the stat blocks above still count every state.
 */
const CITATION_STATE_ORDER: Record<string, number> = {
  live: 0,
  in_progress: 1,
  needs_attention: 2,
};

/** Grouped by state; within a group the provider's own order is kept, so
 * the grid does not reshuffle from one week to the next. */
function sortByState(nodes: ReportCitationNode[]): ReportCitationNode[] {
  const rank = (state: string) => CITATION_STATE_ORDER[state] ?? 3;
  return nodes
    .map((node, index) => ({ node, index }))
    .sort(
      (a, b) => rank(a.node.state) - rank(b.node.state) || a.index - b.index,
    )
    .map(({ node }) => node);
}

/** Matches the design's own default page size (`citDirsAll.slice(0, 8)`,
 * line 934 of the design file) before "Show all" is clicked. */
const NETWORK_PAGE_SIZE = 8;

/** The Citation Network grid — design lines 366-380. Renders nothing (not
 * even the "The Citation Network" heading) when there are no directories to
 * list, the same "say nothing rather than an empty frame" rule the rest of
 * this sub-block follows; the section itself still stands on `ScorePanel`
 * and `StatBlocks` above. */
function CitationNetworkGrid({ nodes }: { nodes: ReportCitationNode[] }) {
  const [expanded, setExpanded] = useState(false);
  if (nodes.length === 0) return null;
  const sorted = sortByState(nodes);
  const visible = expanded ? sorted : sorted.slice(0, NETWORK_PAGE_SIZE);

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: CARD_BORDER,
        borderRadius: '14px',
        p: { xs: 2, sm: 2.75 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: FONT.sectionTitle,
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          The Citation Network
        </Typography>
        <Typography sx={{ fontSize: FONT.fine, color: MUTED_FG }}>
          {visible.length} of {nodes.length} shown
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: 1.5,
        }}
      >
        {visible.map((node, index) => {
          const tone = citationStateTone(node.state);
          return (
            <Box
              key={`${node.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                border: '1px solid',
                borderColor: CARD_BORDER,
                borderRadius: '12px',
                px: 1.75,
                py: 1.5,
                minWidth: 0,
              }}
            >
              <DirectoryMark node={node} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: FONT.body,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {node.name}
                </Typography>
                <Typography sx={{ fontSize: FONT.eyebrow, color: FAINT_FG }}>
                  {node.category}
                </Typography>
              </Box>
              <Box
                component='span'
                sx={{
                  fontSize: FONT.eyebrow,
                  fontWeight: 700,
                  color: tone.fg,
                  bgcolor: tone.bg,
                  px: 1.35,
                  py: 0.55,
                  borderRadius: '9999px',
                  whiteSpace: 'nowrap',
                  flex: '0 0 auto',
                }}
              >
                {CITATION_STATE_LABEL[node.state] || 'Unknown'}
              </Box>
            </Box>
          );
        })}
      </Box>
      {/* `report-no-print`: a reading control, not part of the document a
          client would print or save as a PDF — the same rule every other
          toggle on this page follows. */}
      {nodes.length > NETWORK_PAGE_SIZE && (
        <Box
          component='button'
          type='button'
          className='report-no-print'
          onClick={() => setExpanded(value => !value)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            width: '100%',
            mt: 2,
            border: 0,
            bgcolor: 'transparent',
            fontSize: FONT.body,
            fontWeight: 700,
            color: WORK_POST_FG,
            cursor: 'pointer',
            p: 0,
          }}
        >
          {expanded ? '▴ Show less' : `▾ Show all ${nodes.length} directories`}
        </Box>
      )}
    </Box>
  );
}

/**
 * The Citation Health body `CitationsSection` (ReportSections.tsx) renders
 * once `citations.enabled` is true: the score/stat panel and the Citation
 * Network. The "Foundation" eyebrow and the scanned line are in that
 * section's header, where the design draws them.
 */
export function CitationHealthSection({
  citations,
}: {
  citations: ReportCitations;
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'minmax(0, 1.7fr) minmax(240px, 1fr)',
          },
          gap: 2.25,
          alignItems: 'stretch',
        }}
      >
        <ScorePanel citations={citations} />
        <StatBlocks citations={citations} />
      </Box>
      <CitationNetworkGrid nodes={citations.network} />
    </Box>
  );
}
